import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import swal from "sweetalert";
import CategoryDepartmentModel from "./add&updateCategoryDepartmentModel";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";

const ManageCategoryDepartments = () => {
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "categoryDepartments";
  const defaultVisibleColumns = {
    sortOrder: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "cD_ID", label: "ID" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [departmentToEdit, setDepartmentToEdit] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();



  const sortByOptions = [
    { value: "cD_ID", label: "ID" },
    { value: "cD_Name", label: "Name" },
    { value: "cD_SortOrder", label: "Sort Order" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        APIs.GET_ALL_CATEGORY_DEPARTMENTS,
        {
          params: {
            compID,
            Search: search || "",
            SortBy: sortBy.value,
            SortOrder: sortOrder.value,
          },
        }
      );

      const departmentData = response.data.data || [];
      if (Array.isArray(departmentData)) {
        setDepartments(departmentData);
      } else {

        setDepartments([]);
      }
    } catch (error) {

      setDepartments([]);
      showErrorToast("Failed to fetch departments.");
    } finally {
      setLoading(false);
    }
  }, [compID, search, sortBy, sortOrder]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDepartments();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchDepartments]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const handleDepartmentSubmit = async (departmentData, isUpdate) => {
    try {
      setLoading(true);
      const url = isUpdate
        ? APIs.PUT_UPDATE_CATEGORY_DEPARTMENT
        : APIs.POST_ADD_CATEGORY_DEPARTMENT;
      const method = isUpdate ? "put" : "post";
      const response = await axiosInstance[method](url, {
        ...departmentData,
        compID,
      });
      if (response.data.status) {
        const updatedDepartment = response.data.data;
        if (isUpdate) {
          setDepartments((prev) =>
            prev.map((dept) =>
              dept.cD_ID === updatedDepartment.cD_ID ? updatedDepartment : dept
            )
          );
        } else {
          setDepartments((prev) => [...prev, updatedDepartment]);
        }
        showSuccessToast(`Department ${isUpdate ? "Updated" : "Added"}.`);
      } else {

        showErrorToast(response.data.message || "Operation failed.");
      }
    } catch (error) {

      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartmentClick = () => {
    setDepartmentToEdit(null);
    setShowAddDepartmentModal(true);
  };

  const handleEditDepartmentClick = (department) => {
    setDepartmentToEdit(department);
    setShowAddDepartmentModal(true);
  };

  const DeleteDepartment = async (department) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${department.cD_Name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_CATEGORY_DEPARTMENT,
        {
          params: {
            compID,
            ID: department.cD_ID,
          },
        }
      );
      if (response.data.status) {
        setDepartments((prev) =>
          prev.filter((dept) => dept.cD_ID !== department.cD_ID)
        );
        showSuccessToast("Department is Deleted.");
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Something went wrong.");

    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (departmentId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (departments[i]?.cD_ID) {
            newSelected.add(departments[i].cD_ID);
          }
        }
      } else {
        if (newSelected.has(departmentId)) {
          newSelected.delete(departmentId);
        } else {
          newSelected.add(departmentId);
        }
        lastSelectedIndex.current = index;
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(departments.map((item) => item.cD_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No departments selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected departments?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((departmentId) =>
          axiosInstance.delete(APIs.DELETE_CATEGORY_DEPARTMENT, {
            params: {
              compID,
              ID: departmentId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchDepartments();
      showSuccessToast(
        `${selectedItems.size} departments deleted successfully.`
      );
    } catch (err) {

      showErrorToast("Failed to delete selected departments.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No departments selected for editing.");
      return;
    }

    const selectedDepartments = departments.filter((dept) =>
      selectedItems.has(dept.cD_ID)
    );
    setDepartmentToEdit(selectedDepartments);
    setShowAddDepartmentModal(true);
  };

  const handleDragStart = (e, department) => {
    const index = departments.findIndex((d) => d.cD_ID === department.cD_ID);
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newDepartments = [...departments];
    const draggedDepartment = newDepartments[draggedIndex];
    newDepartments.splice(draggedIndex, 1);
    newDepartments.splice(overIndex, 0, draggedDepartment);
    newDepartments = newDepartments.map((dept, index) => ({
      ...dept,
      cD_SortOrder: index + 1,
    }));

    setDepartments(newDepartments);

    try {
      await axiosInstance.post(
        APIs.POST_UPDATE_CATEGORY_DEPARTMENT_SORT_ORDER,
        {
          compID: compID,
          Updates: newDepartments.map((dept) => ({
            cD_ID: dept.cD_ID,
            cD_SortOrder: dept.cD_SortOrder,
          })),
        }
      );
      fetchDepartments();
      showSuccessToast("Department order updated successfully.");
    } catch (error) {

      showErrorToast("Failed to update department order.");
      fetchDepartments();
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">
              Manage Departments
            </li>
          </ol>
          <h1 className="page-header mb-0">Manage Departments</h1>
        </div>
        <div className="ms-auto">
          {!isChildBranch && (
            <button
              className="btn px-4"
              onClick={handleAddDepartmentClick}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "25px",
                fontWeight: "500"
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
              Add Category Department
            </button>
          )}
        </div>
      </div>

      <div className="mb-3 mt-2">
        {departments.length > 0 && (
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search by name..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when departments are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} department{selectedItems.size > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Departments"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Departments"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    <thead
                      className="bg-light"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              departments.length > 0 &&
                              selectedItems.size === departments.length
                            }
                          />
                        </th>
                        <th className="pt-2 pb-2">Name</th>
                        {visibleColumns.sortOrder && (
                          <th className="pt-2 pb-2 text-center">Sort Order</th>
                        )}
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.map((department, index) => (
                        <tr
                          key={department.cD_ID}
                          draggable={false}
                          onDragStart={(e) => {
                            // Only allow dragging from the grab icon
                            if (!e.target.classList.contains('fa-bars')) {
                              e.preventDefault();
                            }
                          }}
                          onDragEnter={(e) => handleDragEnter(e, index)}
                          onDragLeave={handleDragLeave}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                        >
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(department.cD_ID)}
                              onChange={(e) =>
                                handleSelectItem(department.cD_ID, index, e)
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          <td
                            className="align-middle"
                            onClick={() => {
                              if (isChildBranch) return;
                              handleEditDepartmentClick(department);
                            }}
                            style={{ cursor: isChildBranch ? "default" : "pointer" }}
                          >
                            {department.cD_Name}
                          </td>
                          {visibleColumns.sortOrder && (
                            <td className="align-middle text-center">
                              {department.cD_SortOrder}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Drag Handle */}
                              <i
                                className="fas fa-bars text-secondary"
                                style={{
                                  cursor: "grab",
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #6c757d',
                                  backgroundColor: 'transparent',
                                  transition: 'all 0.2s ease'
                                }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, department)}
                                onDragEnd={handleDragEnd}
                                title="Drag to reorder"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#6c757d';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#6c757d';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              ></i>


                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <CategoryDepartmentModel
        isModalOpen={showAddDepartmentModal}
        setIsModalOpen={setShowAddDepartmentModal}
        onDepartmentSubmit={handleDepartmentSubmit}
        compID={compID}
        departmentToEdit={departmentToEdit}
      />
    </div>
  );
};

export default ManageCategoryDepartments;